import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { ApiLog, OptimizationRun, DebugState } from '@/types/debug';

interface DebugStore extends DebugState {
  // Actions
  toggleDebugPanel: () => void;
  addApiLog: (log: ApiLog) => void;
  addOptimizationRun: (run: OptimizationRun) => void;
  recordPerformance: (metric: string, value: number) => void;
  clearLogs: () => void;
  clearOptimizationHistory: () => void;
  clearPerformanceMetrics: () => void;
  clearAll: () => void;
}

// Create debug store that works in both dev and prod (for mode testing)
export const useDebugStore = create<DebugStore>()(
  devtools(
    (set) => ({
      // Initial state
      isDebugPanelOpen: false,
      apiLogs: [],
      optimizationHistory: [],
      performanceMetrics: new Map(),

      // Actions
      toggleDebugPanel: () => {
        set(state => ({
          isDebugPanelOpen: !state.isDebugPanelOpen
        }));
      },

      addApiLog: (log: ApiLog) => {
        set(state => ({
          apiLogs: [...state.apiLogs, log].slice(-100) // Keep last 100
        }));
      },

      addOptimizationRun: (run: OptimizationRun) => {
        set(state => ({
          optimizationHistory: [...state.optimizationHistory, run].slice(-50) // Keep last 50
        }));
      },

      recordPerformance: (metric: string, value: number) => {
        set(state => {
          const metrics = new Map(state.performanceMetrics);
          const values = metrics.get(metric) || [];
          metrics.set(metric, [...values, value].slice(-100)); // Keep last 100 values
          return { performanceMetrics: metrics };
        });
      },

      clearLogs: () => {
        set({ apiLogs: [] });
      },

      clearOptimizationHistory: () => {
        set({ optimizationHistory: [] });
      },

      clearPerformanceMetrics: () => {
        set({ performanceMetrics: new Map() });
      },

      clearAll: () => {
        set({
          apiLogs: [],
          optimizationHistory: [],
          performanceMetrics: new Map(),
        });
      },
    }),
    { name: 'debug-store' }
  )
);

// Global debug store reference for API client
declare global {
  interface Window {
    __DEBUG_STORE__?: {
      addApiLog: (log: ApiLog) => void;
      addOptimizationRun: (run: OptimizationRun) => void;
      recordPerformance: (metric: string, value: number) => void;
    };
  }
}

// Initialize debug store globally in development
if (import.meta.env.DEV && typeof window !== 'undefined') {
  window.__DEBUG_STORE__ = {
    addApiLog: (log) => {
      try {
        const store = useDebugStore.getState?.();
        if (store) {
          store.addApiLog(log);
        }
      } catch {
        // Store not ready yet, ignore
      }
    },
    addOptimizationRun: (run) => {
      try {
        const store = useDebugStore.getState?.();
        if (store) {
          store.addOptimizationRun(run);
        }
      } catch {
        // Store not ready yet, ignore
      }
    },
    recordPerformance: (metric, value) => {
      try {
        const store = useDebugStore.getState?.();
        if (store) {
          store.recordPerformance(metric, value);
        }
      } catch {
        // Store not ready yet, ignore
      }
    },
  };
}